/**
 * @file     ListingGalleryOrchestrator.tsx
 * @purpose  Gallery state for a listing: ListingHeroBento (tiles, media pills,
 *           Show all), ListingPhotoGrid and GlassLightbox. A photo tap opens
 *           the grid at that photo, a grid tile opens it full size, each
 *           media pill opens its lightbox tab, Show all opens the grid. The lightbox and grid load on first use
 *           (next/dynamic) and are warmed on hover. Inside the slug page's
 *           ListingActionsProvider, the lightbox and grid carry Add to list,
 *           Share and Book a showing on the page's own handlers and CTA config;
 *           elsewhere (quick views) they show photos only.
 */
'use client';

import { useCallback, useMemo, useState } from 'react';
import dynamic from 'next/dynamic';
import type { CtaPosition } from '@/core/config/listingActions';
import type { GallerySource } from '@/core/analytics/listingEvents';
import { useListingEvents } from '@/hooks/useListingEvents';
import { reltaTrack } from '@/hooks/useReltaTrack';
import type { ListingPin } from '@/components/interactive-map/MapboxNeighborhoodMap';
import type { DetailMedia } from '../UnifiedDetail';
import { ListingHeroBento } from './sections/ListingHeroBento';
import { useOptionalListingActions } from './sections/ListingActionsContext';
import type { LightboxActions, LightboxPrimaryId } from './GlassLightbox';
import { streetViewUrl as buildStreetViewUrl, type GalleryTab } from './galleryModel';

const loadLightbox = () => import('./GlassLightbox');
const GlassLightbox = dynamic(() => loadLightbox().then((m) => m.GlassLightbox), { ssr: false });
const ListingPhotoGrid = dynamic(() => import('./sections/ListingPhotoGrid').then((m) => m.ListingPhotoGrid), { ssr: false });

/** Lets the lightbox fade out and release the scroll lock before the next surface opens. */
const AFTER_CLOSE_MS = 200;

interface Props {
  media: DetailMedia[];
  titleAlt: string;
  addressQuery?: string;
  heroSentinelId: string;
  statusChip?: React.ReactNode;
  listingId: string;
  virtualTourUrl?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  /** `compact` for quick views: carousel and thumb strip at every width. */
  variant?: 'page' | 'compact';
}

type LightboxState = { index: number; tab: GalleryTab } | null;

export function ListingGalleryOrchestrator({
  media,
  titleAlt,
  addressQuery,
  heroSentinelId,
  statusChip,
  listingId,
  virtualTourUrl,
  latitude,
  longitude,
  variant = 'page',
}: Props) {
  const [grid, setGrid] = useState<{ start: number } | null>(null);
  const [lightbox, setLightbox] = useState<LightboxState>(null);
  const listingActions = useOptionalListingActions();
  const track = useListingEvents();

  const hasCoords = latitude != null && longitude != null;
  const pin = useMemo<ListingPin | undefined>(
    () =>
      hasCoords
        ? { latitude: latitude!, longitude: longitude!, title: titleAlt, price: null, beds: null, baths: null, sqft: null }
        : undefined,
    [hasCoords, latitude, longitude, titleAlt],
  );
  const streetUrl = buildStreetViewUrl(addressQuery, latitude, longitude);
  const mediaTabs = useMemo(() => {
    const tabs: Exclude<GalleryTab, 'photos'>[] = [];
    if (virtualTourUrl) tabs.push('tour');
    if (streetUrl) tabs.push('street');
    if (pin) tabs.push('map');
    return tabs;
  }, [virtualTourUrl, streetUrl, pin]);

  const openLightbox = useCallback(
    (index: number, tab: GalleryTab = 'photos', source: GallerySource = 'hero') => {
      // One gallery_open per gallery session: a grid tile is already counted
      // by the grid open, in GA4 and in the admin listing report.
      if (source !== 'photo_grid') {
        track('gallery_open', { source, index, listing_key: listingId });
        reltaTrack('listing.gallery.open', { listing_key: listingId });
      }
      setLightbox({ index, tab });
    },
    [listingId, track],
  );
  const closeLightbox = useCallback(() => setLightbox(null), []);
  // A photo tap opens the all-photos grid at that photo (owner, 2026-10-09);
  // a tile in the grid opens it full size.
  const openGrid = useCallback(
    (index = 0, source: GallerySource = 'show_all') => {
      track('gallery_open', { source, index, listing_key: listingId });
      reltaTrack('listing.gallery.open', { listing_key: listingId });
      setGrid({ start: index });
    },
    [listingId, track],
  );
  const closeGrid = useCallback(() => setGrid(null), []);
  const warm = useCallback(() => { void loadLightbox(); }, []);

  let actions: LightboxActions | undefined;
  // Gated previews carry no listing actions (CTA config `icons: false`).
  if (listingActions?.actions.icons) {
    const { actions: resolved, target } = listingActions;
    // The gallery repeats the page's primary visit action: Attend open house
    // when the state leads with it, else Book a showing when the state offers one.
    const primaryId: LightboxPrimaryId | null =
      resolved.primary === 'attend_open_house' || resolved.primary === 'book_showing'
        ? resolved.primary
        : resolved.secondary.includes('book_showing') ? 'book_showing' : null;
    // Sheets sit below the lightbox layer, so the gallery closes first.
    const handOff = (run: () => void) => {
      setLightbox(null);
      setGrid(null);
      window.setTimeout(run, AFTER_CLOSE_MS);
    };
    actions = {
      saved: listingActions.saved,
      onToggleSave: () => listingActions.toggleSave(),
      onShare: listingActions.share,
      primary: primaryId
        ? { id: primaryId, run: (position: CtaPosition) => handOff(() => target(primaryId, position).onClick()) }
        : null,
      onSeeSimilar: () =>
        handOff(() => {
          const similar = target('see_similar', 'gallery_end');
          let stayed = false;
          similar.onClick({ preventDefault: () => { stayed = true; } });
          if (!stayed && similar.href) window.location.assign(similar.href);
        }),
    };
  }

  return (
    <>
      <ListingHeroBento
        media={media}
        titleAlt={titleAlt}
        statusChip={statusChip}
        sentinelId={heroSentinelId}
        mediaTabs={mediaTabs}
        onOpenPhoto={(index) => openGrid(index, 'hero')}
        onOpenTab={(tab) => openLightbox(0, tab, 'media_pill')}
        onShowAll={() => openGrid(0, 'show_all')}
        onIntent={warm}
        compact={variant === 'compact'}
      />

      {grid ? (
        <ListingPhotoGrid
          startIndex={grid.start}
          media={media}
          titleAlt={titleAlt}
          price={listingActions?.listing.price}
          onClose={closeGrid}
          onPhotoClick={(i) => openLightbox(i, 'photos', 'photo_grid')}
          actions={actions}
          suspended={lightbox !== null}
        />
      ) : null}

      {lightbox ? (
        <GlassLightbox
          media={media}
          initialIndex={lightbox.index}
          initialTab={lightbox.tab}
          titleAlt={titleAlt}
          onClose={closeLightbox}
          virtualTourUrl={virtualTourUrl}
          streetViewUrl={streetUrl}
          mapPin={pin}
          actions={actions}
          onTabChange={(tab) => track('gallery_tab', { tab, listing_key: listingId })}
          onEndAction={(action) => track('gallery_end', { action, listing_key: listingId })}
        />
      ) : null}
    </>
  );
}
